import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UserInteractionApiService } from '@angular-workspace/services';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';

export interface ModelCardData {
  id: string;
  title: string;
  organization: string;
  shortDescription?: string;
  industry?: string;
  tags: string[];
  apiTags?: string[];
  modelType?: string;
  language?: string;
  date?: string;
  badge?: string;
  badgeColor?: 'public' | 'private' | 'restricted';
  isNew?: boolean;
  link?: string;
  metrics?: {
    likes?: number;
    downloads?: number;
    files?: number;
    views?: number;
  };
}

@Component({
  selector: 'app-model-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './model-card.component.html',
  styleUrl: './model-card.component.scss',
})
export class ModelCardComponent {
  @Input() card!: ModelCardData;
  /** When true, list view layout: label left + activity right, then createdAt+Public left + arrow right. */
  @Input() showShortDescription: boolean = false;
  /** When true, activity metrics (likes, downloads, views) are shown. Set false e.g. on Features of NHA landing. */
  @Input() showActivityMetrics: boolean = true;
  @Input() isBookmarked: boolean = false;
  @Input() isLiked: boolean = false;
  @Input() isLoggedIn: boolean = false;
  @Output() bookmarkChanged = new EventEmitter<boolean>();
  @Output() likeChanged = new EventEmitter<boolean>();

  private router = inject(Router);
  private userInteractionApiService = inject(UserInteractionApiService);
  private googleAnalytics: GoogleAnalyticsService = inject(GoogleAnalyticsService);

  getBadgeClass(): string {
    if (!this.card.badgeColor) return 'model-card__badge--public';
    return `model-card__badge--${this.card.badgeColor}`;
  }

  onCardClick(): void {
    if (this.card.id) {
      this.googleAnalytics.trackModelClick(this.card.title ?? '', this.card.id);
      this.router.navigate(['/model', this.card.id]);
    } else if (this.card.link) {
      this.router.navigateByUrl(this.card.link);
    }
  }

  async onBookmarkIconClick(event: Event): Promise<void> {
    event.stopPropagation();
    if (!this.isLoggedIn || !this.card?.id) return;
    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.card.id,
        assetType: 'AI_MODEL',
        actionType,
      });
      this.isBookmarked = !this.isBookmarked;
      this.bookmarkChanged.emit(this.isBookmarked);
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  async onLikeIconClick(event: Event): Promise<void> {
    event.stopPropagation();
    if (!this.isLoggedIn || !this.card?.id) return;
    try {
      const actionType = this.isLiked ? 'UNLIKE' : 'LIKE';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.card.id,
        assetType: 'AI_MODEL',
        actionType,
      });
      this.isLiked = !this.isLiked;
      this.likeChanged.emit(this.isLiked);
      if (this.card.metrics) {
        this.card.metrics.likes = this.isLiked
          ? (this.card.metrics.likes || 0) + 1
          : Math.max(0, (this.card.metrics.likes || 0) - 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }
}
